/*
Animated Text
*/

class AnimatedText extends HTMLElement {
  static get observedAttributes() {
    return ["text-center", "animate-by-default", "once", "once-key"];
  }

  constructor() {
    super();
    this._observer = null;
    this._delayPerChar = 0.008;
    this._isVisible = false;
    this._prepared = false;
    this._isPreparing = false;
  }

  connectedCallback() {
    this._ensureStyles();
    this._prepareInPlace();
    this._setupObserver();
  }

  disconnectedCallback() {
    this._teardownObserver();
  }

  attributeChangedCallback() {
    this._ensureHostLayout();
    this._teardownObserver();
    this._setupObserver();
  }

  _getOnceKey() {
    const explicit = this.getAttribute("once-key");
    if (explicit) return `animated_text_once:${explicit}`;

    const section = this.closest("[data-section-id]");
    const sectionId = section
      ? section.getAttribute("data-section-id")
      : "global";

    const h =
      this.closest("[data-product-handle]")?.getAttribute(
        "data-product-handle"
      ) || "unknown";
    return `animated_text_once:${sectionId}:${h}`;
  }

  _shouldAnimateOnce() {
    if (!this.hasAttribute("once")) return true;

    try {
      const key = this._getOnceKey();
      return sessionStorage.getItem(key) !== "1";
    } catch (e) {
      return true;
    }
  }

  _markAnimatedOnce() {
    if (!this.hasAttribute("once")) return;

    try {
      const key = this._getOnceKey();
      sessionStorage.setItem(key, "1");
    } catch (e) {}
  }

  _teardownObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _setupObserver() {
    const animateByDefault = this.hasAttribute("animate-by-default");

    if (!this._shouldAnimateOnce()) {
      this._isVisible = true;
      this.classList.add("is-visible");
      this._markAnimatedOnce();
      return;
    }

    if (animateByDefault) {
      const trigger = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._isVisible = true;
            this.classList.add("is-visible");
            this._markAnimatedOnce();
          });
        });
      };
      if (document.readyState !== "loading") {
        trigger();
      } else {
        document.addEventListener("DOMContentLoaded", trigger, { once: true });
      }
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._isVisible = true;
            this.classList.add("is-visible");
            this._markAnimatedOnce();
            this._teardownObserver();
            break;
          }
        }
      },
      { root: null, threshold: 0, rootMargin: "-15% 0px -15% 0px" }
    );

    this._observer.observe(this);
  }

  _ensureHostLayout() {
    if (this.hasAttribute("text-center")) {
      this.style.display = "block";
      this.style.textAlign = "center";
    } else {
      this.style.display = "";
      this.style.textAlign = "";
    }
  }

  _ensureStyles() {
    this._ensureHostLayout();

    if (this.querySelector(":scope > style[data-animated-text]")) return;

    const style = document.createElement("style");
    style.setAttribute("data-animated-text", "");

    style.textContent = `
      .at-word { display:inline-block; white-space:nowrap; }
      .at-char { display:inline-block; opacity:0; }

      @keyframes atFade { from { opacity:0; } to { opacity:1; } }

      animated-text.is-visible .at-char {
        animation-name: atFade;
        animation-duration: 1.5s;
        animation-timing-function: ease;
        animation-delay: var(--at-delay, 0s);
        animation-fill-mode: forwards;
      }

      @media (prefers-reduced-motion: reduce) {
        animated-text .at-char { opacity: 1 !important; animation: none !important; }
      }
    `;

    this.prepend(style);
  }

  _prepareInPlace() {
    if (this._prepared || this._isPreparing) return;

    if (!this._shouldAnimateOnce()) {
      this._prepared = true;
      this._isPreparing = false;
      this._isVisible = true;
      this.classList.add("is-visible");
      this._markAnimatedOnce();
      return;
    }

    this._isPreparing = true;

    let globalCharIndex = 0;

    const shouldSkip = (node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      const tag = node.tagName.toLowerCase();
      return tag === "style" || tag === "script" || tag === "noscript";
    };

    const wrapTextNode = (textNode) => {
      const text = textNode.nodeValue || "";
      if (!text.trim()) return;

      const tokens = text.split(/(\s+)/);
      const frag = document.createDocumentFragment();

      for (const token of tokens) {
        if (token === "") continue;

        if (/^\s+$/.test(token)) {
          frag.appendChild(document.createTextNode(token));
          continue;
        }

        const wordWrap = document.createElement("span");
        wordWrap.className = "at-word";

        for (const ch of token) {
          const span = document.createElement("span");
          span.className = "at-char";
          span.style.setProperty(
            "--at-delay",
            `${globalCharIndex * this._delayPerChar}s`
          );
          span.textContent = ch;
          wordWrap.appendChild(span);
          globalCharIndex += 1;
        }

        frag.appendChild(wordWrap);
      }

      textNode.parentNode.replaceChild(frag, textNode);
    };

    const walker = document.createTreeWalker(
      this,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!node.nodeValue || !node.nodeValue.trim())
            return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (shouldSkip(parent)) return NodeFilter.FILTER_REJECT;
          if (parent.closest("animated-text") !== this)
            return NodeFilter.FILTER_REJECT;
          if (parent.closest(".at-char,.at-word"))
            return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      },
      false
    );

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    for (const n of nodes) wrapTextNode(n);

    this._prepared = true;
    this._isPreparing = false;

    if (this._isVisible) this.classList.add("is-visible");
  }
}

customElements.define("animated-text", AnimatedText);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }
    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    } else {
      pagerButtons[0].classList.add("active");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.activeBulletScroll;
  }

  connectedCallback() {
    this.activeBulletScroll = this.dataset.activeBulletScroll === "true";

    const rawConfig = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    let config = { ...rawConfig };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, {
      ...config,
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
        bulletClass:
          "w-2 h-2 bg-black rounded-full opacity-20 transition-all duration-200",
        bulletActiveClass: "active",
      },
    });
    this.handleSlideChange();
    this.updateNavButtons();
    this.classList.remove("opacity-0");
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          nextPager.classList.add("active");

          const scrollContainer = pager.parentElement;

          if (scrollContainer && this.activeBulletScroll) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const thumbRect = nextPager.getBoundingClientRect();
            const offset =
              thumbRect.left +
              thumbRect.width / 2 -
              (containerRect.left + containerRect.width / 2);
            const maxScroll =
              scrollContainer.scrollWidth - scrollContainer.clientWidth;
            const targetScroll = Math.min(
              Math.max(0, scrollContainer.scrollLeft + offset),
              maxScroll
            );
            scrollContainer.scrollTo({
              left: targetScroll,
              behavior: "smooth",
            });
          }
        });

        this.updateNavButtons();
      });
    }
  }

  updateNavButtons() {
    const prevButtons = this.querySelectorAll("carousel-prev button");
    const nextButtons = this.querySelectorAll("carousel-next button");

    if (!this.swiper || this.swiper.params.loop) return;

    const isBeginning = this.swiper.isBeginning;
    const isEnd = this.swiper.isEnd;

    prevButtons.forEach((btn) => (btn.disabled = isBeginning));
    nextButtons.forEach((btn) => (btn.disabled = isEnd));
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      const action = this.dataset.action || "cart";

      const atcButton = this.querySelector('button[type="submit"]');
      const atcText = atcButton.innerHTML;

      atcButton.setAttribute("disabled", true);
      atcButton.innerHTML = "Adding to Cart...";

      if ($id) {
        const cart = document.querySelector("site-cart");
        const openCart = action === "cart";

        if (cart) {
          if (openCart) {
            atcButton.innerHTML = atcText;
            atcButton.removeAttribute("disabled");
          }
          await cart.addVariantToCart($id.value, qty, sellingPlan, openCart);
        }

        if (action === "checkout") {
          window.location.href = "/checkout";
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Update Quantity
*/

class UpdateQuantity extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "click",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = this.dataset.index;
        const ctx = this.dataset.ctx;
        const val =
          this.parentElement.querySelector(".curr-quantity").innerHTML;
        let nextVal = Number(val);

        if (ctx === "increment") {
          nextVal += 1;
        } else if (ctx === "decrement") {
          nextVal -= 1;
        }

        if (cart) {
          await cart.updateQuantity(idx, nextVal);
        }
      }, 300)
    );
  }
}

customElements.define("update-quantity", UpdateQuantity);

/*
Cart Update
*/

class CartUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "change",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = e.target.dataset.index;
        const val = e.target.value;

        if (cart) {
          await cart.updateQuantity(idx, val);
        }
      }, 300)
    );
  }
}

customElements.define("cart-update", CartUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this.isCustomer = this.dataset.isCustomer;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
    this.checkUrlForCartOpen();
  }

  checkUrlForCartOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cart_open") === "true") {
      this.openCart();
    }
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
    }
  }

  toggleCart() {
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "header",
        selector: "#header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan, openCart = true) {
    console.log('[ATC Debug]', { id, quantity, sellingPlan, openCart });
    if (openCart) {
      this.openCart();
    }

    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan }
        : { id, quantity },
    ];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    return fetch(`/cart/add.js`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status !== 200) {
          console.error('[ATC Error]', JSON.stringify(response, null, 2));
          return;
        }
        if (response.sections) {
          this.renderCartSections(response.sections);
        }

        return response;
      })
      .catch((e) => {
        console.error('[ATC Catch]', e);
      });
  }

  addVariantToCartWithSubscription(variantId, quantity = 1, planId, openCart) {
    const sellingPlanId = planId;
    return this.addVariantToCart(variantId, quantity, sellingPlanId, openCart);
  }

  getCart() {
    return fetch("/cart.js").then((response) => response.json());
  }

  updateQuantity(line, quantity) {
    const items = Array.from(document.querySelectorAll(".cart-item"));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);
    const item = items[line - 1];
    const prices = item.querySelectorAll(".cart-price");
    const loader = item.querySelector(".cart-loader");

    prices.forEach((p) => p.classList.add("opacity-0"));
    if (loader) {
      loader.classList.remove("opacity-0");
    }

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then(async (response) => {
        this.renderCartSections(response.sections);
        this.updateMemberVisibility();
        await this.removeMemberProductsIfNoBundleOrCustomer();

        prices.forEach((p) => p.classList.remove("opacity-0"));
        if (loader) {
          loader.classList.add("opacity-0");
        }
      })
      .catch(() => {
        // handle error
      });
  }
  async swapCartItem(lineIndex, variantId, quantity, sellingPlanId) {
    // Step 1: Remove old item (no section render)
    await fetch(
      "/cart/change.js",
      this.buildConfig(
        JSON.stringify({ line: parseInt(lineIndex), quantity: 0 })
      )
    );

    // Step 2: Add new item with section render
    const items = [
      { id: parseInt(variantId), quantity: parseInt(quantity || 1) },
    ];
    if (sellingPlanId) items[0].selling_plan = parseInt(sellingPlanId);
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });

    const result = await fetch("/cart/add.js", this.buildConfig(body));
    const response = await result.json();
    if (response.status) {
      return;
    }
    this.renderCartSections(response.sections);
  }
}

customElements.define("site-cart", SiteCart);

/*
Cart Upsell
*/

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.querySelector("[data-upsell-action]")?.addEventListener("click", () =>
      this.handleAction()
    );
    this.querySelector("[data-upsell-dismiss]")?.addEventListener("click", () =>
      this.handleDismiss()
    );
  }

  async handleAction() {
    const btn = this.querySelector("[data-upsell-action]");
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Updating...";

    const siteCart = document.querySelector("site-cart");
    const lineIndex = this.dataset.lineIndex;

    // For OTP variant with radio options
    const radio = this.querySelector('input[name="upsell-option"]:checked');
    const variantId = radio?.dataset.variantId || this.dataset.variantId;
    const sellingPlanId =
      radio?.dataset.sellingPlanId || this.dataset.sellingPlanId || null;
    const quantity = radio?.dataset.quantity || this.dataset.quantity || 1;

    try {
      await siteCart.swapCartItem(
        lineIndex,
        variantId,
        quantity,
        sellingPlanId
      );
    } catch (e) {
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  handleDismiss() {
    this.style.display = "none";
  }
}

customElements.define("cart-upsell", CartUpsell);

/*
Plan Selector (Cart)
*/

class CartPlanSelector extends HTMLElement {
  connectedCallback() {
    this.options = this.querySelectorAll("[data-plan-option]");
    this.options.forEach((option) => {
      option.addEventListener("click", () => this.selectOption(option));
    });

    const upgradeBtn = this.querySelector("[data-plan-upgrade]");
    if (upgradeBtn) {
      upgradeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleUpgrade();
      });
    }

    const dismissBtn = this.querySelector("[data-plan-dismiss]");
    if (dismissBtn) {
      dismissBtn.addEventListener("click", () => {
        this.style.display = "none";
        const reviews = this.parentElement?.querySelector(
          "[data-plan-reviews]"
        );
        if (reviews) reviews.style.display = "none";
      });
    }
  }

  selectOption(option) {
    this.options.forEach((o) => o.classList.remove("active"));
    option.classList.add("active");
  }

  async handleUpgrade() {
    const active = this.querySelector("[data-plan-option].active");
    if (!active) return;

    // If Alia trigger option, open popup instead of swapping
    if (active.hasAttribute("data-alia-trigger")) {
      if (window.alia) window.alia.open();
      return;
    }

    const variantId = active.dataset.variantId;
    if (!variantId) return;

    const siteCart = document.querySelector("site-cart");
    const lineIndex = this.dataset.lineIndex;
    const sellingPlanId = active.dataset.sellingPlanId || null;
    const quantity = active.dataset.quantity || 1;

    await siteCart.swapCartItem(lineIndex, variantId, quantity, sellingPlanId);
  }
}

customElements.define("cart-plan-selector", CartPlanSelector);

/*
Mobile Menu
*/

class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.drawer = this.querySelector("[data-menu-drawer]");
    this.overlay = this.querySelector("[data-menu-overlay]");
    this.openBtn = this.querySelector("[data-menu-open]");
    this.closeBtn = this.querySelector("[data-menu-close]");

    this.openBtn?.addEventListener("click", () => this.open());
    this.closeBtn?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", () => this.close());

    // Close on Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    });

    // Close when nav link clicked (anchor links on same page)
    this.querySelectorAll("[data-menu-link]").forEach((link) => {
      link.addEventListener("click", () => this.close());
    });

    // Close menu after ATC
    this.querySelectorAll(
      "[data-menu-drawer] cart-add button[type='submit']"
    ).forEach((btn) => {
      btn.addEventListener("click", () => {
        setTimeout(() => this.close(), 300);
      });
    });

    this.initProductCard();
  }

  open() {
    this.isOpen = true;

    // Position drawer just below the announcement bar if visible
    const announcement = document.querySelector(".announcement");
    if (announcement) {
      const bottom = announcement.getBoundingClientRect().bottom;
      this.drawer.style.top = Math.max(0, bottom) + "px";
    } else {
      this.drawer.style.top = "0px";
    }

    this.drawer.classList.remove("-translate-x-full");
    this.drawer.style.boxShadow = "4px 0 24px rgba(0,0,0,0.08)";
    this.overlay?.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.add("-translate-x-full");
    this.drawer.style.boxShadow = "none";
    this.overlay?.classList.add("hidden");
    document.body.style.overflow = "";
  }

  initProductCard() {
    const dataEl = this.querySelector("[data-menu-variant-data]");
    if (!dataEl) return;

    const variants = JSON.parse(dataEl.textContent);
    let selectedIndex = 0;
    let isSubscription = true;

    const variantBtns = this.querySelectorAll("[data-menu-variant-btn]");
    const purchaseBtns = this.querySelectorAll("[data-menu-purchase-btn]");
    const variantInput = this.querySelector("[data-menu-variant-input]");
    const sellingPlanInput = this.querySelector(
      "[data-menu-selling-plan-input]"
    );
    const productImage = this.querySelector("[data-menu-product-image]");
    const subPriceEl = this.querySelector("[data-menu-sub-price]");
    const otpPriceEl = this.querySelector("[data-menu-otp-price]");

    const updateVariant = (index) => {
      selectedIndex = index;
      const v = variants[index];

      variantBtns.forEach((btn, i) => {
        if (i === index) {
          btn.classList.add("bg-white", "shadow-sm", "text-black");
          btn.classList.remove("text-black/40");
        } else {
          btn.classList.remove("bg-white", "shadow-sm", "text-black");
          btn.classList.add("text-black/40");
        }
      });

      if (subPriceEl && v.sellingPlanPriceFormatted) {
        subPriceEl.textContent = v.sellingPlanPriceFormatted + "/mo";
      }
      if (otpPriceEl) {
        otpPriceEl.textContent = v.priceFormatted;
      }

      variantInput.value = v.id;
      if (isSubscription && v.sellingPlanId) {
        sellingPlanInput.value = v.sellingPlanId;
      }
    };

    const updatePurchaseType = (type) => {
      isSubscription = type === "subscribe";
      const v = variants[selectedIndex];

      purchaseBtns.forEach((btn) => {
        const btnType = btn.dataset.menuPurchaseBtn;
        if (btnType === type) {
          btn.classList.add("bg-white", "shadow-sm");
          btn.classList.remove("text-black/40");
        } else {
          btn.classList.remove("bg-white", "shadow-sm");
          btn.classList.add("text-black/40");
        }
      });

      if (isSubscription && v.sellingPlanId) {
        sellingPlanInput.value = v.sellingPlanId;
      } else {
        sellingPlanInput.value = "";
      }
    };

    variantBtns.forEach((btn, i) => {
      btn.addEventListener("click", () => updateVariant(i));
    });

    purchaseBtns.forEach((btn) => {
      btn.addEventListener("click", () =>
        updatePurchaseType(btn.dataset.menuPurchaseBtn)
      );
    });
  }
}

customElements.define("mobile-menu", MobileMenu);

/*
Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  constructor() {
    super();
    this.context = this.dataset.context;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.submitForm(e);
      });
    }
  }

  async submitForm(e) {
    const error = this.querySelector(".error");
    const success = this.querySelector(".success");
    const emailOrPhone = this.form.querySelector('[type="text"]');

    const submit = this.form.querySelector('[type="submit"]');
    const listId = this.dataset.listId;

    error.innerHTML = "";

    console.log("### submit", submit);

    const submitText = submit.value;
    submit.value = "...";

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: emailOrPhone.value,
                subscriptions: {
                  email: { marketing: { consent: "SUBSCRIBED" } },
                },
              },
            },
          },
        },
        ...(listId && {
          relationships: { list: { data: { type: "list", id: listId } } },
        }),
      },
    };

    try {
      const resp = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=RrtvEE`,
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: "2025-01-15",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(
          errorData.errors?.[0]?.detail || "Klaviyo subscription request failed"
        );
      }

      this.form.reset();
      submit.value = submitText;
      success.classList.remove("hidden");
      emailOrPhone.blur();
    } catch (e) {
      error.classList.remove("hidden");
      error.innerHTML =
        e.message || "Oops, there was an error. Please try again.";
      submit.value = submitText;
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  connectedCallback() {
    this.summaryOnly = this.dataset.summaryOnly === "true";
    this.startOpen = this.dataset.startOpen === "true";
    this.rotate180 = this.dataset.rotate180 === "true";

    this.icon = this.querySelector(".rotate-element");
    this.details = this.querySelector("details");
    this.content = this.querySelector(".content");
    this.summary = this.querySelector("summary");

    this.content.style.transition = "none";

    if (this.startOpen) {
      this.details.open = true;
      this.content.style.maxHeight = this.content.scrollHeight + "px";
      if (this.rotate180 === true) {
        this.icon.style.transform = "rotate(180deg)";
      } else {
        this.icon.style.transform = "rotate(90deg)";
      }
    } else {
      this.content.style.maxHeight = "0px";
    }

    requestAnimationFrame(() => {
      this.content.style.transition = "max-height 0.3s ease";
    });

    const target = this.summaryOnly ? this.summary : this.details;
    target.addEventListener("click", (e) => {
      e.preventDefault();
      this.animateHeight();
    });
  }

  animateHeight() {
    const isOpen = this.details.open;

    if (isOpen) {
      this.icon.style.transform = "rotate(0deg)";
      this.content.style.maxHeight = "0px";
      setTimeout(() => {
        this.details.open = false;
      }, 300);
    } else {
      this.details.open = true;
      if (this.rotate180 === true) {
        this.icon.style.transform = "rotate(180deg)";
      } else {
        this.icon.style.transform = "rotate(90deg)";
      }
      this.content.style.maxHeight = this.content.scrollHeight + "px";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Site Modal
*/

class SiteModal extends HTMLElement {
  connectedCallback() {
    this.registerEvents();
    this.defineAutoOpenRoutine();
    this.modal = this.querySelector(".modal");
  }

  openModal() {
    if (this.modal) {
      document.body.classList.add("overflow-hidden");
      this.modal.classList.add("active");
    }
  }

  closeModal() {
    if (this.modal) {
      document.body.classList.remove("overflow-hidden");
      this.modal.classList.remove("active");
    }
  }

  registerEvents() {
    const opens = this.querySelectorAll(".modal-open");
    const closes = this.querySelectorAll(".modal-close");
    const triggerClass = this.dataset.trigger;
    const triggers = document.querySelectorAll(triggerClass);

    if (triggers) {
      triggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          const agreeError = document.querySelector(".agree-error");
          if (agreeError) {
            agreeError.classList.add("hidden");

            if (e.target.classList.contains("select-subscription")) {
              if (!document.getElementById("agreeWithSubscription").checked) {
                agreeError.classList.remove("hidden");
                return;
              }
            }
          }

          this.openModal();
        });
      });
    }

    Array.from(opens).forEach((open) => {
      open.addEventListener("click", (e) => {
        this.openModal();
      });
    });

    Array.from(closes).forEach((close) => {
      close.addEventListener("click", (e) => {
        this.closeModal();
      });
    });
  }

  defineAutoOpenRoutine() {
    const autoOpenTimeout = this.dataset.autoOpenTimeout;
    if (
      autoOpenTimeout &&
      !localStorage.getItem("feno-subscribe") &&
      !sessionStorage.getItem("feno-subscribe")
    ) {
      setTimeout(() => {
        this.openModal();
      }, autoOpenTimeout);
    }
  }
}

customElements.define("site-modal", SiteModal);

class CarouselResults extends HTMLElement {
  constructor() {
    super();
    this.buttons = [];
    this.slides = [];
    this.buttonsWrapper = null;
  }

  connectedCallback() {
    this.buttons = this.querySelectorAll("[filter-data]");
    this.slides = this.querySelectorAll("[result-category]");
    this.buttonsWrapper = this.querySelector(".carousel-buttons");

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.setActiveButton(button);
        this.filterSlides(button.getAttribute("filter-data"));
      });
    });

    const defaultButton =
      [...this.buttons].find(
        (btn) => btn.getAttribute("filter-data")?.toLowerCase() === "all"
      ) || this.buttons[0];

    if (defaultButton) {
      this.setActiveButton(defaultButton);
      this.filterSlides(defaultButton.getAttribute("filter-data"));
    }
  }

  normalize(value) {
    return value?.toLowerCase().trim();
  }

  setActiveButton(activeBtn) {
    this.buttons.forEach((btn) => {
      btn.classList.remove("bg-black", "text-white");
      btn.classList.add("hover:bg-pink", "hover:text-white");
    });

    activeBtn.classList.add("bg-black", "text-white");
    activeBtn.classList.remove("hover:bg-pink", "hover:text-white");
  }

  filterSlides(filterValue) {
    const filter = this.normalize(filterValue);

    this.slides.forEach((slide) => {
      const category = this.normalize(slide.getAttribute("result-category"));

      const shouldShow = filter === "all" || filter === category;

      slide.classList.toggle("hidden", !shouldShow);
    });

    this.updateButtonsVisibility();
    this.nextDisabledState();
    this.updateSwiper();
  }

  updateButtonsVisibility() {
    if (!this.buttonsWrapper) return;

    const visibleButtons = [...this.slides].filter(
      (btn) => !btn.classList.contains("hidden")
    );

    const shouldHide = visibleButtons.length <= 1;

    this.buttonsWrapper.classList.toggle("hidden", shouldHide);
  }

  nextDisabledState() {
    const visibleSlides = [...this.slides].filter(
      (slide) => !slide.classList.contains("hidden")
    );

    if (visibleSlides.length > 1) {
      const nextBtn = this.querySelector("button.next");
      if (nextBtn?.hasAttribute("disabled")) {
        nextBtn.removeAttribute("disabled");
      }
    }
  }

  updateSwiper() {
    const swiperEl = this.querySelector(".swiper");

    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.update();
    }
  }
}

customElements.define("carousel-results", CarouselResults);

class ReviewsToggle extends HTMLElement {
  constructor() {
    super();
    this.boundToggle = this.toggle.bind(this);
    this.observer = null;
    this.targetSelector = "[data-oke-widget] .okeReviews";
  }

  static get observedAttributes() {
    return ["closed"];
  }

  get isClosed() {
    return this.hasAttribute("closed");
  }

  get button() {
    return this.querySelector("button");
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener("click", this.boundToggle);
      this.updateButtonText();
    }

    this.initObserver();

    this.applyStateToTarget();
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener("click", this.boundToggle);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "closed" && oldValue !== newValue) {
      this.updateButtonText();
      this.applyStateToTarget();
    }
  }

  initObserver() {
    this.observer = new MutationObserver((mutations) => {
      const target = document.querySelector(this.targetSelector);

      if (target) {
        const isHidden = target.style.display === "none";
        const shouldBeHidden = this.isClosed;

        if (isHidden !== shouldBeHidden) {
          this.applyStateToTarget();
        }
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  toggle() {
    if (this.isClosed) {
      this.removeAttribute("closed");
    } else {
      this.setAttribute("closed", "");
    }
  }

  applyStateToTarget() {
    const target = document.querySelector(this.targetSelector);
    if (!target) return;

    if (this.isClosed) {
      target.style.display = "none";
    } else {
      target.style.display = "block";
    }
  }

  updateButtonText() {
    if (!this.button) return;

    const text = this.isClosed ? "See All Reviews" : "See Less";
    this.button.textContent = text;
  }
}

customElements.define("reviews-toggle", ReviewsToggle);

/*
Plan Selector — 4-option accordion with multi-product ATC support
*/

class PlanSelector extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector("[data-plan-data]");
    if (!dataEl) return;

    this.planData = JSON.parse(dataEl.textContent);
    this.cards = [...this.querySelectorAll("[data-plan-index]")];

    const url = new URL(window.location);
    const defaultPlan = parseInt(this.dataset.defaultPlan || "1");
    this.activePlan = parseInt(url.searchParams.get("plan") || defaultPlan);
    if (this.activePlan > this.planData.plans.length) this.activePlan = defaultPlan;

    this.applyState();
    this.attachListeners();
  }

  attachListeners() {
    this.cards.forEach((card) => {
      card.addEventListener("click", (e) => {
        if (e.target.closest(".alia-popup-trigger")) return;
        if (e.target.closest("button[type='submit']")) return;
        const aliaLink = e.target.closest('a[href="#alia"]');
        if (aliaLink) {
          e.preventDefault();
          if (window.alia) window.alia.open();
          return;
        }
        this.selectPlan(parseInt(card.dataset.planIndex));
      });
    });
  }

  selectPlan(index) {
    if (this.activePlan === index) return;
    this.activePlan = index;

    const url = new URL(window.location);
    url.searchParams.set("plan", index);

    const plan = this.planData.plans[index - 1];
    let sellingPlanId = plan.sellingPlanId;

    // In cadence mode, get selling plan from the variantMap entry for current flavor
    if (plan.variantMap && this.planData.currentFlavor) {
      const entry = plan.variantMap[this.planData.currentFlavor];
      if (entry) sellingPlanId = entry.sellingPlanId;
    }

    if (sellingPlanId) {
      url.searchParams.set("selling_plan", sellingPlanId);
    } else {
      url.searchParams.delete("selling_plan");
    }
    window.history.replaceState({}, "", url);

    this.applyState();
  }

  applyState() {
    const selectedBorder = ["border-[3px]", "border-[#DD2863]"];
    const unselectedBorder = ["border-[3px]", "border-transparent"];

    this.cards.forEach((card, i) => {
      const isActive = i + 1 === this.activePlan;
      // const accordion = card.querySelector("[data-plan-accordion]");
      const radio = card.querySelector("[data-plan-radio]");
      const dot = card.querySelector("[data-plan-radio-dot]");

      if (isActive) {
        card.classList.remove(...unselectedBorder);
        card.classList.add(...selectedBorder);
      } else {
        card.classList.remove(...selectedBorder);
        card.classList.add(...unselectedBorder);
      }

      if (radio) {
        radio.classList.toggle("border-[#DD2863]", isActive);
        radio.classList.toggle("border-black/[0.08]", !isActive);
      }
      if (dot) {
        dot.classList.toggle("bg-[#DD2863]", isActive);
        dot.classList.toggle("bg-transparent", !isActive);
      }

      /* Accordion disabled — benefits always visible. Uncomment to re-enable:
      if (accordion) {
        if (isActive) {
          accordion.classList.remove("max-h-0", "opacity-0");
          accordion.classList.add("max-h-[500px]", "opacity-100");
        } else {
          accordion.classList.remove("max-h-[500px]", "opacity-100");
          accordion.classList.add("max-h-0", "opacity-0");
        }
      }
      */
    });

    this.updateForm();
  }

  updateForm() {
    const form = document.querySelector("#pdp-add-cart form");
    if (!form) return;

    const plan = this.planData.plans[this.activePlan - 1];
    if (!plan) return;

    const variantInput = form.querySelector('[name="id"]');
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');

    let variantId = plan.variantId;
    let sellingPlanId = plan.sellingPlanId || "";

    // Cadence mode: variantMap values are { variantId, sellingPlanId } objects
    if (plan.variantMap && this.planData.currentFlavor) {
      const entry = plan.variantMap[this.planData.currentFlavor];
      if (entry) {
        variantId = entry.variantId;
        sellingPlanId = entry.sellingPlanId || "";
      }
    }

    if (variantInput) variantInput.value = variantId;
    if (sellingPlanInput) sellingPlanInput.value = sellingPlanId;

    const atcBtn = form.querySelector("[data-atc-button]");
    const isSubscription = !!sellingPlanId;
    if (atcBtn) {
      const textEl = atcBtn.querySelector("[data-atc-text]");
      const label = isSubscription
        ? atcBtn.dataset.textSubscribe
        : atcBtn.dataset.textOnetime;
      if (textEl && label) textEl.textContent = label;
    }
  }
}

customElements.define("plan-selector", PlanSelector);

class ProductDetailsController extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;
    this.defaultSubscriptionOption =
      this.dataset.defaultSubscriptionOption === "true";
    this.firstInteraction = true;

    this.addVariantChangeEvents();
    this.addSubscribeChangeEvents();
  }

  async updateProductDetailsHTML() {
    const url = new URL(window.location);

    const variantParam = url.searchParams.get("variant")
      ? `variant=${url.searchParams.get("variant")}`
      : "";
    const sellingPlanParam = url.searchParams.get("selling_plan")
      ? `&selling_plan=${url.searchParams.get("selling_plan")}`
      : "";

    const focusEl = document.activeElement;

    const rawPathWithParams = `/products/${this.productHandle}?sections=${this.sectionId}&${variantParam}${sellingPlanParam}`;

    const rawResponse = await fetch(rawPathWithParams);
    const response = await rawResponse.json();

    const sectionHTML = response[this.sectionId];
    const element = this.htmlToElement(sectionHTML);

    if (!element) return;

    const nextGallery = element.querySelector("#pdp-gallery");
    const nextDetails = element.querySelector("#product-details");

    const currentGallery = this.querySelector("#pdp-gallery");
    const currentDetails = this.querySelector("#product-details");

    if (nextGallery && currentGallery) {
      currentGallery.innerHTML = nextGallery.innerHTML;
    }

    if (nextDetails && currentDetails) {
      currentDetails.innerHTML = nextDetails.innerHTML;
    }

    this.addSubscribeChangeEvents();
    this.addVariantChangeEvents();

    if (focusEl && focusEl.id) {
      const nextFocus = document.getElementById(focusEl.id);
      nextFocus && nextFocus.focus();
    }
  }

  addVariantChangeEvents() {
    const variantsSelectorEl = this.querySelectorAll(
      "[name='variant-selector']"
    );

    if (!variantsSelectorEl.length) return;

    variantsSelectorEl.forEach((option) => {
      option.addEventListener("change", () => {
        const url = new URL(window.location);
        url.searchParams.set("variant", option.value);
        window.history.replaceState({}, "", url);

        if (this.defaultSubscriptionOption && this.firstInteraction) {
          this.firstInteraction = false;
          this.setSellingPlanSearchParam();
        }

        this.updateProductDetailsHTML();
      });
    });
  }

  addSubscribeChangeEvents() {
    if (this.querySelector("plan-selector")) return;

    const purchaseOptions = this.querySelectorAll("[name='purchase-options']");
    if (!purchaseOptions.length) return;

    purchaseOptions.forEach((option) => {
      option.addEventListener("change", () => {
        const url = new URL(window.location);

        if (option.id == "subscribe-purchase") {
          this.setSellingPlanSearchParam();
        } else {
          url.searchParams.delete("selling_plan");
          url.searchParams.set("variant", option.dataset.sellingPlanVariantId);
          window.history.replaceState({}, "", url);
        }

        this.updateProductDetailsHTML();
      });
    });
  }

  setSellingPlanSearchParam() {
    const subscribePurchaseEl = this.querySelector("#subscribe-purchase");
    if (!subscribePurchaseEl) return;

    const url = new URL(window.location);
    url.searchParams.set(
      "selling_plan",
      subscribePurchaseEl.dataset.sellingPlanId
    );
    window.history.replaceState({}, "", url);
  }

  htmlToElement(html) {
    var template = document.createElement("template");
    html = html.trim();
    template.innerHTML = html;
    return template.content.firstChild;
  }
}

customElements.define("product-details-controller", ProductDetailsController);

class StickyBar extends HTMLElement {
  connectedCallback() {
    this.shouldShowScroll = false;
    this.shouldShowTrigger = false;

    const selector = this.getAttribute("trigger-selector");
    setTimeout(() => {
      this.triggerElement = document.querySelector(selector);
      this.initScroll();
      this.initViewportTracking();
      if (this.triggerElement) {
        this.initObserver();
      } else {
        this.shouldShowTrigger = true;
      }
    }, 150);
  }

  initViewportTracking() {
    if (!window.visualViewport) return;
    const update = () => {
      const vv = window.visualViewport;
      const offset = window.innerHeight - (vv.height + vv.offsetTop);
      this.style.bottom = offset > 0 ? `${offset}px` : "0px";
    };
    window.visualViewport.addEventListener("resize", update, { passive: true });
    window.visualViewport.addEventListener("scroll", update, { passive: true });
  }

  initObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isTriggerOnScreen = entry.isIntersecting;
          if (this.isTriggerOnScreen) {
            this.shouldShowTrigger = false;
          } else {
            this.shouldShowTrigger = true;
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(this.triggerElement);
  }

  initScroll() {
    document.addEventListener(
      "scroll",
      () => {
        requestAnimationFrame(() => {
          const totalHeight = document.documentElement.scrollHeight;
          const scrollPosition = window.scrollY + window.innerHeight;
          const distanceToBottom = totalHeight - scrollPosition;

          const scrollThreshold =
            parseInt(this.getAttribute("scroll-threshold")) || 0;
          if (
            window.scrollY > scrollThreshold &&
            distanceToBottom > this.clientHeight * 3
          ) {
            this.shouldShowScroll = true;
          } else {
            this.shouldShowScroll = false;
          }

          const shouldShow = this.shouldShowScroll && this.shouldShowTrigger;
          this.showStickyBar(shouldShow);
        });
      },
      { passive: true }
    );
  }

  showStickyBar(shouldShow) {
    if (shouldShow) {
      this.classList.remove("translate-y-full");
      this.classList.add("translate-y-0");
    } else {
      this.classList.remove("translate-y-0");
      this.classList.add("translate-y-full");
    }
  }
}

customElements.define("sticky-bar", StickyBar);

class StickyPlanDropdown extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector("[data-sticky-plan-data]");
    if (!dataEl) return;

    this.planData = JSON.parse(dataEl.textContent);

    this.trigger = this.querySelector("[data-dropdown-trigger]");
    this.panel = this.querySelector("[data-dropdown-panel]");
    this.label = this.querySelector("[data-dropdown-label]");
    this.chevron = this.querySelector("[data-dropdown-chevron]");
    this.options = [...this.querySelectorAll("[data-plan-option]")];
    this.isOpen = false;

    this.activePlan = this.planData.defaultPlan || 1;
    this.applyState();
    this.updateForm();

    this.trigger.addEventListener("click", () => this.toggle());

    this.options.forEach((opt) => {
      opt.addEventListener("click", () => {
        this.selectPlan(parseInt(opt.dataset.planOption));
      });
    });

    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.contains(e.target)) this.close();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.panel.classList.remove("hidden");
    this.chevron.classList.add("rotate-180");
  }

  close() {
    this.isOpen = false;
    this.panel.classList.add("hidden");
    this.chevron.classList.remove("rotate-180");
  }

  selectPlan(index) {
    if (this.activePlan === index) {
      this.close();
      return;
    }
    this.activePlan = index;
    this.applyState();
    this.updateForm();
    this.close();
  }

  getPlanData(plan) {
    if (plan.variantMap && this.planData.currentFlavor) {
      const entry = plan.variantMap[this.planData.currentFlavor];
      if (entry) return entry;
    }
    return plan;
  }

  getPlanLabel(plan) {
    return plan.name || plan.label;
  }

  getPlanSave(plan) {
    const pct = plan.savePercent;
    if (pct && pct > 0) return "Save " + pct + "%";
    return "";
  }

  applyState() {
    const plan = this.planData.plans[this.activePlan - 1];
    if (plan && this.label) {
      let triggerText = this.getPlanLabel(plan);
      const save = this.getPlanSave(plan);
      if (save) triggerText += " (" + save + ")";
      this.label.textContent = triggerText;
    }

    this.options.forEach((opt) => {
      const idx = parseInt(opt.dataset.planOption);
      const isActive = idx === this.activePlan;
      const radio = opt.querySelector("[data-option-radio]");
      const dot = opt.querySelector("[data-option-dot]");
      const labelEl = opt.querySelector("[data-option-label]");
      const saveEl = opt.querySelector("[data-option-save]");

      if (radio) {
        radio.classList.toggle("border-[#DD2863]", isActive);
        radio.classList.toggle("border-[#D1D5DB]", !isActive);
      }
      if (dot) {
        dot.classList.toggle("bg-[#DD2863]", isActive);
        dot.classList.toggle("bg-transparent", !isActive);
      }

      const p = this.planData.plans[idx - 1];
      if (p && labelEl) labelEl.textContent = this.getPlanLabel(p);
      if (p && saveEl) saveEl.textContent = this.getPlanSave(p);
    });
  }

  updateForm() {
    const stickyBar = this.closest("sticky-bar");
    if (!stickyBar) return;

    const form = stickyBar.querySelector("cart-add form");
    if (!form) return;

    const plan = this.planData.plans[this.activePlan - 1];
    if (!plan) return;

    const data = this.getPlanData(plan);
    const variantId = data.variantId || plan.variantId;
    const sellingPlanId = data.sellingPlanId || plan.sellingPlanId || "";
    const formattedPrice = data.formattedPrice || plan.formattedPrice;
    const suffix = this.planData.priceSuffix || "";

    const variantInput = form.querySelector('[name="id"]');
    if (variantInput && variantId) variantInput.value = variantId;

    let sellingPlanInput = form.querySelector('[name="selling_plan"]');
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement("input");
      sellingPlanInput.type = "hidden";
      sellingPlanInput.name = "selling_plan";
      form.appendChild(sellingPlanInput);
    }
    sellingPlanInput.value = sellingPlanId;

    const atcBtn = form.querySelector('button[type="submit"]');
    if (atcBtn && formattedPrice) {
      atcBtn.textContent = "ADD TO CART \u00B7 " + formattedPrice + suffix;
    }
  }
}

customElements.define("sticky-plan-dropdown", StickyPlanDropdown);

class TimelineScroll extends HTMLElement {
  constructor() {
    super();
    this.dots = [];
  }

  connectedCallback() {
    this.dots = [...this.querySelectorAll("[data-timeline-dot]")];
    this.wrapper = this.querySelector("[data-timeline-wrapper]");

    if (!this.dots.length || !this.wrapper) return;

    window.addEventListener("scroll", () => this.update());
    window.addEventListener("resize", () => this.update());

    this.update();
  }

  update() {
    const rect = this.wrapper.getBoundingClientRect();
    const total = rect.height - window.innerHeight;

    const progress = Math.min(Math.max(-rect.top / total, 0), 1);

    const activeIndex = Math.floor(progress * this.dots.length);

    this.dots.forEach((dot, index) => {
      if (index === activeIndex) {
        this.activate(dot);
      } else {
        this.deactivate(dot);
      }
    });

    if (activeIndex >= this.dots.length) {
      this.activate(this.dots[this.dots.length - 1]);
    }
  }

  activate(dot) {
    dot.style.backgroundColor = "#dd2863";
    dot.style.color = "#ffffff";
  }

  deactivate(dot) {
    dot.style.backgroundColor = "#ffffff";
    dot.style.color = "rgba(0,0,0,0.6)";
  }
}

/*
 * Button animation
 * Drives --border-rot and --bg-pos so both animate forward on hover
 * and continue forward (full circle / full cycle) on hover-off rather than reversing.
 *
 * --bg-pos math: background-size is 400%, gradient has 2 visual cycles.
 * One visual cycle = 66.67% of --bg-pos. Hover goes ~75% of one cycle (50%),
 * leave continues to 66.67% (same visual as 0%), then snaps back invisibly.
 */
(function () {
  const BASE_ROT = 135;
  const HOVER_ROT = 261; // ~30% of 360° forward
  const LEAVE_ROT = 495; // full circle forward back to start (135 + 360)

  const BASE_POS = "0%";
  const HOVER_POS = "40%"; // ~60% of one visual cycle forward
  const LEAVE_POS = "66.67%"; // one full visual cycle (same appearance as 0%)

  function initButton(btn) {
    let leaveHandler = null;

    btn.addEventListener("mouseenter", () => {
      if (leaveHandler) {
        btn.removeEventListener("transitionend", leaveHandler);
        leaveHandler = null;
      }
      btn.style.setProperty("--border-rot", HOVER_ROT);
      btn.style.setProperty("--bg-pos", HOVER_POS);
    });

    btn.addEventListener("mouseleave", () => {
      btn.style.setProperty("--border-rot", LEAVE_ROT);
      btn.style.setProperty("--bg-pos", LEAVE_POS);

      leaveHandler = function (e) {
        // --bg-pos is the slower property (400ms); wait for it before snapping
        if (e.propertyName !== "--bg-pos") return;
        btn.removeEventListener("transitionend", leaveHandler);
        leaveHandler = null;

        // Snap both back to base (visually identical positions) without animating
        btn.style.transition = "none";
        btn.style.setProperty("--border-rot", BASE_ROT);
        btn.style.setProperty("--bg-pos", BASE_POS);
        btn.getBoundingClientRect(); // force reflow to commit the snap
        btn.style.transition = "";
      };

      btn.addEventListener("transitionend", leaveHandler);
    });
  }

  document
    .querySelectorAll(
      ".button--primary, .button--dark-pink, .button--light-pink"
    )
    .forEach(initButton);
})();

customElements.define("timeline-scroll", TimelineScroll);
